import { Trans, useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import WorkshopList, { WorkshopListEmptyState } from '@/components/WorkshopList';
import { WorkshopListItem } from '@/components/WorkshopList/WorkshopList';
import AlertBar from '@/design_system/AlertBar';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { hasActions, useActivities } from '@/models/article';
import { canActOnBehalfOfNonDigitalizedWorkshop } from '@/models/user';
import { useWorkshops } from '@/models/workshop';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useCurrentSession } from '@/services/auth';

import { useRequestContext } from '../contexts/RequestContext';

export const ArticleDispatch = () => {
  const { article, view, errors } = useArticleContext();

  const { t } = useLingui();

  const enableWorkshopSelection = view.services.dispatchBeforeActions || hasActions(article);

  const { data: { workshops } = { workshops: [] }, isFetching: isFetchingWorkshops } = useWorkshops(
    {
      articleId: article.id,
      limit: 100,
    },
    {
      enabled: enableWorkshopSelection || !!article.workshopId,
      keepPreviousData: true,
    }
  );

  const { data: { activities: jobRefusedActivities } = { activities: [] } } = useActivities({
    articleId: article.id,
    types: ['job_refused'],
  });

  const selectedWorkshop = workshops.find((workshop) => workshop.id === article.workshopId);

  if (!view.services.dispatch.editable) {
    return (
      <Stack gap="0.25rem" className="text-primary">
        <p className="label-100-medium">
          <Trans id="article.service-choice.dispatch.assigned.label">Assigned workshop</Trans>
        </p>
        {selectedWorkshop && <WorkshopListItem workshop={selectedWorkshop} isDisabled />}
        {!selectedWorkshop && (
          <Paragraph>
            <Trans id="article.service-choice.dispatch.assigned.none">None</Trans>
          </Paragraph>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="2rem">
      <Stack gap="0.25rem">
        <p className="label-100-medium text-primary">
          <Trans id="article.service-choice.dispatch.selector.label">
            Select a workshop to handle the job
          </Trans>
        </p>

        {!enableWorkshopSelection && !hasActions(article) && (
          <Message type="info">
            <Trans id="article.service-choice.dispatch.no-actions">
              Please set the repair actions before selecting the workshop.
            </Trans>
          </Message>
        )}

        {enableWorkshopSelection && workshops.length > 0 && (
          <WorkshopList
            selected={article.workshopId ?? undefined}
            workshops={workshops}
            jobRefusedActivities={jobRefusedActivities}
            isFetchingWorkshops={isFetchingWorkshops}
          />
        )}

        {enableWorkshopSelection &&
          workshops.length === 0 &&
          (isFetchingWorkshops ? (
            <Loader style={{ marginTop: '0.5rem' }} />
          ) : (
            <WorkshopListEmptyState
              emptyStateText={t({
                id: 'article.service-choice.dispatch.no-workshop-found',
                message:
                  "We couldn't find any workshop for this kind of care and repair job needs.",
              })}
            />
          ))}

        {enableWorkshopSelection && errors.services?.dispatch?.noWorkshopSelected && (
          <Message type="error">
            <Trans id="article.service-choice.dispatch.error.no-workshop-selected">
              Please select a workshop to handle the job.
            </Trans>
          </Message>
        )}
      </Stack>

      {!!selectedWorkshop?.workshopConfig?.nonDigitalized && <NonDigitalizedWorkshopMessage />}
    </Stack>
  );
};

const NonDigitalizedWorkshopMessage = () => {
  const { currentSession } = useCurrentSession();
  const { request, workflow } = useRequestContext();

  const canActOnBehalfOfWorkshop = canActOnBehalfOfNonDigitalizedWorkshop({
    user: currentSession,
    request,
    workflow,
  });

  if (canActOnBehalfOfWorkshop) {
    return null;
  }

  return (
    <AlertBar type="warning" size="large">
      <Trans id="article.service-choice.dispatch.warning.non-digitalized">
        By choosing a non-digitalized workshop, tasks will need to be marked as completed on its
        behalf.
      </Trans>
    </AlertBar>
  );
};
